import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {ItemCreatedResponse} from '../../entities/base/item-created-response';
import {Appointment} from './entities/appointment';
import {AppointmentCollectionResponse} from './entities/appointment-collection-response';
import {AppointmentType} from './entities/appointment-type';
import {AppointmentTypeCollectionResponse} from './entities/appointment-type-collection-response';
import {CalendarAppointment} from './entities/calendar-appointment';
import {CalendarAppointmentCollectionResponse} from './entities/calendar-appointment-collection-response';
import {DatePipe} from '@angular/common';
import {ApiService} from '../base/api.service';
import {ItemUpdatedResponse} from '../../entities/base/item-updated-response';
import {AdminApiService} from '../base/admin-api.service';
import {PatientStatus} from '../patient-status-repository/entities/patient-status';
import {ItemDeletedResponse} from '../../entities/base/item-deleted-response';
import {AppointmentNotification} from '../appointment-notification-settings-repository/entities/appointment-notification';
import {AppointmentNotificationCollectionResponse} from '../appointment-notification-settings-repository/entities/appointment-notification-collection-response';
import {AppointmentClash} from './entities/appointment-clash';
import {AppointmentClashCollectionResponse} from './entities/appointment-clash-collection-response';
import {normalizeRecurrenceRule, SeriesScope} from './entities/recurrence';

/**
 * Rewrite each appointment's raw `recurrenceRule` into the normalised shape.
 *
 * Done here, at the one boundary the wire crosses, so no component ever sees the raw
 * value — the read path emits enum ordinals while the write path takes names, and
 * normalizeRecurrenceRule is the single place that knows it. Non-recurring appointments
 * pass through untouched.
 */
function normalizeAppointments(items: CalendarAppointment[]): CalendarAppointment[] {
  return (items ?? []).map(item => item.recurrenceRule
    ? {...item, recurrenceRule: normalizeRecurrenceRule(item.recurrenceRule)}
    : item);
}

@Injectable({
  providedIn: 'root'
})
export class PatientAppointmentsRepositoryService {

  constructor(private datePipe: DatePipe, private api: ApiService, private adminApi: AdminApiService) {
  }

  loadAppointments(startDate: string, endDate: string, practiceLocationId: number, practicePractitionerId: number): Observable<CalendarAppointment[]> {
    return this.api.get<CalendarAppointmentCollectionResponse>('/appointments', {
      startDate,
      endDate,
      practiceLocationId,
      practicePractitionerId
    }).pipe(map(response => normalizeAppointments(response.items)));
  }

  /**
   * This practitioner's existing appointments that overlap the given time slot, across
   * all locations. `start`/`end` are zone-less practice wall-clock strings (the same form
   * sent on the write path). `excludeAppointmentId` drops the appointment being edited so
   * it doesn't clash with itself. Read-only and intentionally non-blocking — the caller
   * warns, it never prevents save.
   */
  findClashes(
    practitionerId: number,
    start: string,
    end: string,
    excludeAppointmentId?: number
  ): Observable<AppointmentClash[]> {
    const params: Record<string, string | number> = {practitionerId, start, end};
    if (excludeAppointmentId != null) {
      params['excludeAppointmentId'] = excludeAppointmentId;
    }
    return this.api.get<AppointmentClashCollectionResponse>('/appointments/clashes', params)
      .pipe(map(response => response.items));
  }

  loadAppointmentTypes(): Observable<AppointmentType[]> {
    return this.api.get<AppointmentTypeCollectionResponse>('/appointments/types')
      .pipe(map(response => response.items));
  }

  loadAppointmentsByDay(date: Date, practiceLocationId?: number, practicePractitionerId?: number): Observable<CalendarAppointment[]> {
    const dateString = this.datePipe.transform(date, 'yyyy-MM-dd');
    return this.api.get<CalendarAppointmentCollectionResponse>('/appointments/day/' + dateString, {
      practiceLocationId,
      practicePractitionerId
    }).pipe(map(response => normalizeAppointments(response.items)));
  }

  loadPatientAppointments(patientId: number): Observable<Appointment[]> {
    return this.api.get<AppointmentCollectionResponse>('/appointments/patient/' + patientId)
      .pipe(map(response => response.items));
  }

  addAppointmentToPatient(patientId: number, appointment: Appointment): Observable<boolean> {
    return this.api.post<ItemCreatedResponse>('/appointments/patient/' + patientId, appointment)
      .pipe(map(response => response.status.toLocaleUpperCase() === 'OK'));
  }

  addAppointment(appointment: Appointment): Observable<boolean> {
    return this.api.post<ItemCreatedResponse>('/appointments/', appointment)
      .pipe(map(response => response.status.toLocaleUpperCase() === 'OK'));
  }

  updateAppointment(appointment: Appointment): Observable<boolean> {
    return this.api.put<ItemUpdatedResponse>('/appointments/' + appointment.id, appointment)
      .pipe(map(response => response.status.toLocaleUpperCase() === 'OK'));
  }

  setAppointmentConfirmed(appointmentId: number, confirmed: boolean): Observable<boolean> {
    return this.api.put<ItemUpdatedResponse>('/appointments/' + appointmentId + '/confirmed', {confirmed})
      .pipe(map(response => response.status.toLocaleUpperCase() === 'OK'));
  }

  /**
   * Cancel an appointment. `scope` only matters for an occurrence of a series: omitted
   * (the default) cancels just this one, 'ThisAndFollowing' also cancels every later
   * occurrence. An unrecognised value is rejected by the server rather than narrowed, so
   * only ever pass a SeriesScope.
   */
  deleteAppointment(appointmentId: number, scope?: SeriesScope): Observable<boolean> {
    // setParams drops undefined, so a scope-less call sends no query string at all —
    // byte-identical to what every caller sent before scopes existed.
    return this.api.delete<ItemUpdatedResponse>('/appointments/' + appointmentId, {scope})
      .pipe(map(response => response.status.toLocaleUpperCase() === 'OK'));
  }


  adminLoadAppointmentTypes(): Observable<AppointmentType[]> {
    return this.adminApi.get<AppointmentTypeCollectionResponse>('/appointments/types')
      .pipe(map(response => response.items));
  }

  adminCreate(type: AppointmentType): Observable<number> {
    return this.adminApi.post<ItemCreatedResponse>('/appointments/types', type)
      .pipe(map(response => response.value));
  }

  adminUpdate(id: number, type: AppointmentType): Observable<boolean> {
    return this.adminApi.put<ItemUpdatedResponse>('/appointments/types/' + id, type)
      .pipe(map(response => response.status.toLocaleUpperCase() === 'OK'));
  }

  adminDelete(id: number): Observable<boolean> {
    return this.adminApi.delete<ItemDeletedResponse>('/appointments/types/' + id)
      .pipe(map(response => response.status.toLocaleUpperCase() === 'OK'));
  }

  loadAppointmentNotifications(appointmentId: number): Observable<AppointmentNotification[]> {
    return this.api.get<AppointmentNotificationCollectionResponse>(
      '/appointments/' + appointmentId + '/notifications'
    ).pipe(map(response => response.items));
  }
}
